import { Truck as TruckIcon, Plane as PlaneIcon } from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────────
   Shared design tokens and render-only helpers for the create-order desks
   (forward and reverse). Tokens follow the shipment-details page exactly: solid
   #3877FC accent (#2A59BD on hover), slate everywhere else, emerald only for done.
   Nothing here touches form state or payloads.
──────────────────────────────────────────────────────────────────────────── */
export const CARD = "rounded-2xl border border-[#E4E9F1] border-b-[#D8DFEA] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_-16px_rgba(15,23,42,0.10),0_24px_48px_-24px_rgba(30,58,138,0.10)]";

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/* A stable SKU suggestion from a product name - the same name always suggests
   the same id, which is exactly what keeps one product from growing five SKUs. */
export const suggestSkuFromName = (name) =>
  String(name || "").toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 24);

/* Saved-location display helpers (render-only): source data often arrives in
   all-caps - title-case it for reading; initials feed the monogram chip. */
export const titleCaseLoc = (v) => String(v || "").toLowerCase().replace(/(^|[^a-z0-9])([a-z])/g, (m, pre, ch) => pre + ch.toUpperCase());
export const displayLocName = (v) => { const t = String(v || "").trim(); return t && t === t.toUpperCase() ? titleCaseLoc(t) : t; };
export const locInitials = (v) => {
  const w = String(v || "").trim().split(/\s+/).filter(Boolean);
  return ((w[0]?.[0] || "") + (w[1]?.[0] || w[0]?.[1] || "")).toUpperCase();
};

export const LABEL = "block text-[13px] font-semibold text-slate-800 mb-1.5";
export const SPRING = { type: "spring", stiffness: 320, damping: 32 };

export const inputClass = (hasError, extra = "") =>
  `h-11 w-full rounded-xl border bg-white px-3.5 text-sm font-semibold text-slate-900 placeholder:font-medium placeholder:text-slate-500 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none transition-[border-color,box-shadow] duration-150 ` +
  `focus:border-[#3877FC] focus:shadow-[0_0_0_4px_rgba(56,119,252,0.12)] ` +
  `${hasError ? "border-rose-300 bg-rose-50/40" : "border-[#D6DEE9] hover:border-[#AFBDD1]"} ${extra}`;

export const BTN_PRIMARY = "group inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-xl bg-[#3877FC] px-5 text-sm font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_1px_2px_rgba(15,23,42,0.10),0_8px_20px_-8px_rgba(56,119,252,0.50)] transition-all duration-150 hover:bg-[#2A59BD] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_10px_24px_-8px_rgba(42,89,189,0.55)] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40 focus-visible:ring-offset-2 disabled:opacity-60 disabled:shadow-none";
export const BTN_OUTLINE = "inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-xl bg-white px-4 text-sm font-medium text-slate-600 shadow-[0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-inset ring-[#D8DFEA] transition-all duration-150 hover:bg-[#F8FAFD] hover:text-slate-900 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 disabled:opacity-60";
export const BTN_GHOST = "group inline-flex h-11 items-center gap-1.5 rounded-xl px-3.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 cursor-pointer";

/* <input type="number"> accepts "e", "+", "-" (and repeats of ".") as "number characters" — the
   box shows "10-e" while the DOM reports value "" and nothing validates. These guards keep such
   keys out at the source; the blur hook catches whatever a browser still lets through. */
export const numericKeyGuard = (integer = false) => (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  const k = e.key;
  if (k === "e" || k === "E" || k === "+" || k === "-") { e.preventDefault(); return; }
  if (k === "." && (integer || String(e.currentTarget.value).includes("."))) e.preventDefault();
};
export const numericPasteGuard = (integer = false) => (e) => {
  const text = (e.clipboardData && e.clipboardData.getData("text")) || "";
  if (!(integer ? /^\d+$/ : /^\d*\.?\d*$/).test(text.trim())) e.preventDefault();
};
export const fmtInr = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const initialsOf = (name) =>
  String(name || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "•";

/* Courier presentation helpers (manual selection) ───────────────────────────── */
// A raw name like "EKART SURFACE" carries the carrier and its service mode; split them
// so each can be styled. Carriers title-case (EKART → Ekart) but short acronyms (DTDC)
// stay upper.
export const CARRIER_MODES = ["SURFACE", "AIR", "EXPRESS", "PREMIUM", "STANDARD", "HEAVY", "ECONOMY", "PRIORITY", "SECURE", "LITE"];
export const formatCarrier = (raw) => {
  // "REVERSE" / "RVP" in a reverse-lane name only repeats what the desk already says; drop it.
  const parts = String(raw || "").trim().split(/\s+/).filter(Boolean).filter((w) => !/^(REVERSE|RVP)$/i.test(w));
  let mode = null;
  if (parts.length > 1 && CARRIER_MODES.includes(parts[parts.length - 1].toUpperCase())) {
    const m = parts.pop();
    mode = m.charAt(0).toUpperCase() + m.slice(1).toLowerCase();
  }
  const carrier = parts
    .map((w) => (w.length <= 4 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(" ");
  return { carrier: carrier || String(raw || "").trim(), mode };
};
// A stable, tasteful accent per carrier (soft-tinted avatar) so the list reads as distinct
// brands without shouting. Selection stays the app blue.
export const CARRIER_ACCENTS = ["#3877FC", "#6D5EF6", "#0EA5A6", "#D9643B", "#DB4A6B", "#2563EB", "#0E9F6E", "#0891B2"];
export const carrierAccent = (name) => {
  let h = 0;
  const s = String(name || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CARRIER_ACCENTS[h % CARRIER_ACCENTS.length];
};
// Service mode → a soft tinted chip + a mode glyph (ground vs air), lighter than the
// Cheapest/Fastest ranking pills so the ranking stays the primary signal.
export const MODE_STYLE = {
  Surface: "bg-slate-100 text-slate-600 ring-slate-200/80",
  Air: "bg-sky-50 text-sky-700 ring-sky-100",
  Express: "bg-amber-50 text-amber-700 ring-amber-100",
  Premium: "bg-violet-50 text-violet-700 ring-violet-100",
  Priority: "bg-rose-50 text-rose-700 ring-rose-100",
};
export const MODE_ICON = { Surface: TruckIcon, Air: PlaneIcon };
// TAT (max days from pickup) → a tangible "latest by" date, compact and consistent
// (guaranteed 3-letter month/day, so rows stay tidy on mobile). "By" already implies latest.
export const WEEKDAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const estDeliveryDate = (days) => {
  const n = Number(days);
  if (!Number.isFinite(n) || n < 0) return null;
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${WEEKDAY_ABBR[d.getDay()]}, ${d.getDate()} ${MONTH_ABBR[d.getMonth()]}`;
};
export const relativeDays = (n) => (n <= 0 ? "today" : n === 1 ? "tomorrow" : `in ${n} days`);
// A rate that carries no turnaround time is shown as "Usually 5 days from pickup" — the word
// "usually" is the tell that the figure is assumed, not the courier's — and sorts as 5 under Fastest.
export const ASSUMED_TAT_DAYS = 5;

export function fmtStampDate(d) {
  try {
    const day = String(d.getDate()).padStart(2, "0");
    const mon = MONTH_ABBR[d.getMonth()];
    let h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, "0");
    const ap = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${day} ${mon} ${d.getFullYear()} · ${h}:${m} ${ap}`;
  } catch { return null; }
}

export const riseIn = (reduce, delay) => (reduce
  ? { initial: false }
  : { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { delay, duration: 0.42, ease: [0.22, 1, 0.36, 1] } });
